import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, BehaviorSubject } from 'rxjs';
import { tap } from 'rxjs/operators';
import { WindowRef } from '@services/window-ref.service';
import { GenericHttp } from '@services/remote-api/generic-http.service';
import { LoginApiService } from '@services/remote-api';
import { SseService } from '@services/remote-api/sse.service';
import { CookiesService } from '@services/cookies.service';
import { Config } from '@config/config.service';
import { AccountService } from './account.service';

@Injectable()
export class LoginService {
  redirectUrl: any;
  redirectParams: any;
  authChange: Observable<boolean>;
  authChangeSource: BehaviorSubject<boolean>;

  window: Window;
  logoutPagePath: string;
  baseUrl: string;

  accessTokenKey: string;
  constructor(
    private genericHttp: GenericHttp,
    private loginApi: LoginApiService,
    private cookiesService: CookiesService,
    private config: Config,
    private router: Router,
    private winRef: WindowRef,
    private accountService: AccountService,
    private sseService: SseService
  ) {
    this.baseUrl = config.baseUrl;
    this.window = winRef.nativeWindow;
    this.redirectUrl = `${config.pages.profile}/${config.pages.dashboard}`;
    this.accessTokenKey = config.accessTokenKey;
    this.logoutPagePath = config.pages.login;

    this.authChangeSource = new BehaviorSubject<boolean>(this.isLoggedIn);
    this.authChange = this.authChangeSource.asObservable();

    this.handleAccessErr();
  }

  get isLoggedIn(): boolean {
    return Boolean(this.cookiesService.getCookie(this.accessTokenKey));
  }

  sendPasswordEmail(data: any): Observable<any> {
    return this.loginApi.sendPasswordEmail(data);
  }

  changePassword(data: any): Observable<any> {
    return this.loginApi.changePassword(data).pipe(tap(this.handleLogin.bind(this)));
  }

  signin(userData: any): Observable<any> {
    return this.loginApi.signin(userData).pipe(tap(this.handleLogin.bind(this)));
  }

  signInAzure(data): Observable<any> {
    return this.loginApi.signInAzure(data).pipe(tap(this.handleLogin.bind(this)));
  }

  logout(callback?: any, redirectUrl: string = this.logoutPagePath, refreshRedirection = true): void {
    const url = redirectUrl;

    this.sseService.unsubscribe();
    this.accountService.profileData = null;
    this.accountService.companyUsers = null;
    this.cookiesService.deleteCookie('accessToken');
    this.cookiesService.deleteCookie(this.config.accessTokenKey);
    this.changeAuth(false);
    this.router.navigate([url]);
    if (refreshRedirection) {
      this.redirectUrl = `${this.config.pages.profile}/${this.config.pages.dashboard}`;
    }
    this.redirectParams = '';
    if (typeof callback === 'function') {
      callback();
    }
  }

  changeAuth(logged: boolean) {
    this.authChangeSource.next(logged);
  }

  handleLogin(response: any): void {
    this.cookiesService.setCookie(this.accessTokenKey, response.id_token);
    this.changeAuth(true);
  }

  successNavigate(): any {
    const url = this.redirectUrl;

    this.accountService.setProfileData();
    this.router.navigate([url], this.redirectParams);
  }

  errorNavigate(): void {
    if (this.isLoggedIn) {
      const url = this.redirectUrl;

      this.router.navigate([url]);
    } else {
      const url = this.config.pages.login;

      this.router.navigate([url]);
    }
  }

  private handleAccessErr(): void {
    this.genericHttp.accessError.subscribe(accessError => {
      if (accessError) {
        this.logout(null, this.config.pages.login, false);
      }
    });
  }
}
